import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { saveRoadmapData } from '../../services/roadmap.service';
import { useAuth } from '../../hooks/useAuth';
import { 
    MdArrowForward, MdArrowBack, MdCheckCircle, MdSave
} from 'react-icons/md';
import './RoadmapForm.css';

const RoadmapForm = () => {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);

    const [formData, setFormData] = useState({
        reportId: reportId || '',
        section1: { fullName: '', age: '', city: '', profession: '', dependents: [], jobStability: 'Very Stable', careerChanges: '' },
        section2: { monthlySalary: '', annualCTC: '', basic: '', hra: '', allowance: '', bonus: '', rentEMI: '', groceries: '', utilities: '', transport: '', schoolFees: '', diningOut: '', shopping: '', subscriptions: '', travel: '', insurancePremiums: '', loanEMIs: '', monthlySurplus: '', surplusDestination: '' },
        section3: { 
            homeLoanAmount: '', homeLoanInterest: '', homeLoanEMI: '', 
            personalLoanAmount: '', personalLoanInterest: '', personalLoanEMI: '', 
            carLoanAmount: '', carLoanInterest: '', carLoanEMI: '', 
            creditCardDues: '', 
            epf: 'No', ppfAmount: '', 
            sipAmount: '', sipFundType: '', 
            stocks: 'No', stocksAmount: '', 
            fdBondsAmount: '', 
            lifeInsuranceType: 'Term', lifeInsuranceCover: '', lifeInsurancePremium: '', 
            healthInsuranceType: 'Individual', healthInsuranceCover: '', healthInsuranceParentsRecord: 'No', healthInsurancePremium: '' 
        },
        section4: { emergencyFund: 'No', emergencyFundAmount: '', emergencyFundMonths: '3', emergencyFundStorage: 'Savings', criticalIllnessCover: 'No', accidentCover: 'No' },
        section5: { shortTermGoals: [], mediumTermGoals: [], retirementAge: '', retirementLifestyle: 'Comfortable', retirementCorpus: '', childrenFuturePlans: '', topGoalsRank: '' },
        section6: { investmentDropReaction: 'Hold', financialKnowledge: 3, pastMistake: '', bestDecision: '' },
        section7: { taxRegime: 'New', declaredToHR: 'No' },
        deepDiveA: { perfectDay: '', financialFreedomMeaning: '', magicWand: '', biggestWorry: '', whatMakesYouFeelRich: '' },
        deepDiveB: { dreamTrip: '', parentsDream: '', partnerChildrenExperiences: '', career10Years: '', passionProject: '', idealHome: '', representationMilestone: '' },
        deepDiveC: { childhoodMoneyLearning: '', biggestFinancialFear: '', generosityLegacy: '', fomoPressure: '' },
        deepDiveD: { enabling5Years: '', timelineDreams: '', top3Tradeoffs: '' },
        vision: { lifeMovieTrailer: '', unknownFailAction: '', perfectSunday: '', billboardMessage: '', havingItAllMeaning: '', instantOwnThing: '', peacePlace: '', onceInLifeExperience: '' },
        loveLegacy: { parentsDreamSpecific: '', childrenMemoryDesperate: '', generousPersonTarget: '', traditionRecreate: '', perfectMilestoneCelebration: '' },
        ambitions: { skillToLearn: '', causeToSupport: '', retirementSecretDream: '', adventureYearDesign: '', admireInOthers: '' },
        inventory: { debtFreeHome: 5, familyInternationalTrip: 5, retireBefore55: 5, startBusiness: 5, childrenCollegeAbroad: 5, dreamCar: 5, travelSabbatical: 5, parentsRetirementSupport: 5, secondHome: 5, expensiveHobby: 5, quitJobFreedom: 5, legacyInheritance: 5 },
        summary: { celebrationProgress: '' }
    });

    const steps = [
        { title: "Personal Profile", short: "Profile", subtitle: "(The Basics)" },
        { title: "Income & Cash Flow", short: "Income", subtitle: "(The Snapshot)" },
        { title: "Assets & Liabilities", short: "Wealth", subtitle: "(Existing Assets)" },
        { title: "Protection & Net", short: "Protection", subtitle: "(The Immune System)" },
        { title: "Goals & Dreams", short: "Dreams", subtitle: "(The Destination)" },
        { title: "Mindset & Tax", short: "Human", subtitle: "(The Human Element)" },
        { title: "The 'Why'", short: "Why", subtitle: "(Deep Dive)" },
        { title: "Lifestyle & Purpose", short: "Purpose", subtitle: "(Beyond Basics)" },
        { title: "Vision & Deep Desires", short: "Vision", subtitle: "(Painting the Picture)" },
        { title: "Love & Legacy", short: "Legacy", subtitle: "(Heart & Relations)" },
        { title: "Inventory & Summary", short: "Final", subtitle: "(Desire Survey)" }
    ];

    const totalSteps = steps.length;
    const completionPercent = Math.round(((currentStep - 1) / totalSteps) * 100);

    // AUTO-SAVE LOGIC
    useEffect(() => {
        const savedData = localStorage.getItem(`roadmap_${reportId}`);
        if (savedData) {
            try {
                const parsed = JSON.parse(savedData);
                setFormData(parsed);
            } catch (err) { console.error("Restore failed", err); }
        }
    }, [reportId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            localStorage.setItem(`roadmap_${reportId}`, JSON.stringify(formData));
            setLastSaved(new Date().toLocaleTimeString());
        }, 1000);
        return () => clearTimeout(timer);
    }, [formData, reportId]);

    const formatLabel = (key) => {
        if (!key) return "";
        if (['hra', 'epf', 'ppf', 'sip', 'fd', 'ctc', 'emi'].includes(key.toLowerCase())) return key.toUpperCase();
        return key.replace(/([A-Z])/g, ' $1').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ').trim();
    };

    const handleChange = (section, field, value) => {
        setFormData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    };

    const handleMultiSelect = (section, field, value) => {
        const currentArr = formData[section][field] || [];
        const newArr = currentArr.includes(value) ? currentArr.filter(item => item !== value) : [...currentArr, value];
        handleChange(section, field, newArr);
    };

    const nextStep = (e) => {
        e.preventDefault(); // Preventive measure
        if (currentStep < totalSteps) { 
            setCurrentStep(currentStep + 1); 
            window.scrollTo(0, 0); 
        } 
    };
    
    const prevStep = (e) => { 
        e.preventDefault(); // Preventive measure
        if (currentStep > 1) { 
            setCurrentStep(currentStep - 1); 
            window.scrollTo(0, 0); 
        } 
    };

    const [expandedSections, setExpandedSections] = useState([1]); // For Step 2 accordion

    const toggleSection = (id) => {
        setExpandedSections(prev => 
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setIsSubmitting(true);
        try {
            await saveRoadmapData({ ...formData, userId: user?.uid || user?.id, submittedAt: new Date().toISOString() });
            localStorage.removeItem(`roadmap_${reportId}`);
            alert('Roadmap Form Submitted Successfully! 🎉'); // ONLY ON ACTUAL SUBMIT
            navigate(`/diagnosis-report/${reportId}`);
        } catch (error) {
            console.error(error);
            alert('Submission failed. Please check your connection.');
        } finally { setIsSubmitting(false); }
    };

    const renderStepContent = () => {
        const step = steps[currentStep - 1];

        if (currentStep === 1) {
            return (
                <div className="doc-card">
                    <div className="form-grid">
                        <div className="form-group"><label>Full Name</label><input type="text" value={formData.section1.fullName} onChange={(e) => handleChange('section1', 'fullName', e.target.value)} /></div>
                        <div className="form-group"><label>Age</label><input type="number" value={formData.section1.age} onChange={(e) => handleChange('section1', 'age', e.target.value)} /></div>
                        <div className="form-group full-width"><label>Profession & City</label><input type="text" value={formData.section1.profession} onChange={(e) => handleChange('section1', 'profession', e.target.value)} /></div>
                    </div>
                    <div className="form-group" style={{marginTop: '2rem'}}>
                        <label>Financially Dependent?</label>
                        <div className="multi-select-group">
                            {['Spouse', 'Children', 'Parents'].map(dep => (
                                <button key={dep} type="button" className={`doc-pill ${formData.section1.dependents.includes(dep) ? 'active' : ''}`} onClick={() => handleMultiSelect('section1', 'dependents', dep)}>{dep}</button>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 2) {
            return (
                <div className="doc-card-accordion">
                    {/* Part 1: Salary */}
                    <div className={`accordion-group ${expandedSections.includes(1) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(1)}>
                            <h3>1. Gross Monthly/Annual Salary</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-grid">
                                <div className="form-group"><label>Total Monthly Salary (In-hand)</label><input type="number" value={formData.section2.monthlySalary} onChange={(e) => handleChange('section2', 'monthlySalary', e.target.value)} /></div>
                                <div className="form-group"><label>Total Annual CTC</label><input type="number" value={formData.section2.annualCTC} onChange={(e) => handleChange('section2', 'annualCTC', e.target.value)} /></div>
                            </div>
                            <p className="doc-hint" style={{marginTop: '1rem', color: '#64748b'}}>Salary Split (Optional but helpful):</p>
                            <div className="form-grid four-col">
                                {['basic', 'hra', 'allowance', 'bonus'].map(f => (
                                    <div key={f} className="form-group"><label>{formatLabel(f)}</label><input type="number" value={formData.section2[f]} onChange={(e) => handleChange('section2', f, e.target.value)} /></div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Part 2: Expenses */}
                    <div className={`accordion-group ${expandedSections.includes(2) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(2)}>
                            <h3>2. Monthly Expense Breakdown</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="expense-category">
                                <label className="cat-label"><strong>Necessities:</strong> (Rent/EMI, Groceries, Utilities, Transport, Fees)</label>
                                <div className="form-grid three-col">
                                    {['rentEMI', 'groceries', 'utilities', 'transport', 'schoolFees'].map(f => (
                                        <div key={f} className="form-group"><label>{formatLabel(f)}</label><input type="number" value={formData.section2[f]} onChange={(e) => handleChange('section2', f, e.target.value)} /></div>
                                    ))}
                                </div>
                            </div>

                            <div className="expense-category" style={{marginTop: '2rem'}}>
                                <label className="cat-label"><strong>Discretionary/Lifestyle:</strong> (Dining, Shopping, Subscriptions, Hobbies, Travel)</label>
                                <div className="form-grid three-col">
                                    {['diningOut', 'shopping', 'subscriptions', 'travel'].map(f => (
                                        <div key={f} className="form-group"><label>{formatLabel(f)}</label><input type="number" value={formData.section2[f]} onChange={(e) => handleChange('section2', f, e.target.value)} /></div>
                                    ))}
                                </div>
                            </div>

                            <div className="form-grid" style={{marginTop: '2rem'}}>
                                <div className="form-group"><label><strong>Insurance Premiums:</strong> (Health, Life, Vehicle)</label><input type="number" value={formData.section2.insurancePremiums} onChange={(e) => handleChange('section2', 'insurancePremiums', e.target.value)} placeholder="Total monthly cost for all insurance" /></div>
                                <div className="form-group"><label><strong>Existing EMIs:</strong> (Personal, Car, Credit Card Dues)</label><input type="number" value={formData.section2.loanEMIs} onChange={(e) => handleChange('section2', 'loanEMIs', e.target.value)} placeholder="Total monthly EMI outflow" /></div>
                            </div>
                        </div>
                    </div>

                    {/* Part 3: Surplus */}
                    <div className={`accordion-group ${expandedSections.includes(3) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(3)}>
                            <h3>3. What is your "untracked surplus"?</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>After all expenses and investments, how much money is typically left in your bank account? Where does it usually go?</label>
                                <textarea value={formData.section2.surplusDestination} onChange={(e) => handleChange('section2', 'surplusDestination', e.target.value)} placeholder="e.g. 15,000 left, usually spent on impulse shopping or stays in savings..." />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 11) {
            return (
                <div className="doc-card">
                    <h3 className="card-sub">Quick Desire Inventory (1-10)</h3>
                    <div className="rating-grid-premium">
                        {Object.keys(formData.inventory).map(key => (
                            <div key={key} className="rating-card-lux">
                                <label>
                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: 
                                    <span className="val-hi">{formData.inventory[key]}</span>
                                </label>
                                <input type="range" min="1" max="10" value={formData.inventory[key]} onChange={(e) => handleChange('inventory', key, e.target.value)} className="luxury-slider-dyn" />
                            </div>
                        ))}
                    </div>
                    <h3 className="card-sub" style={{marginTop: '4rem'}}>The One-Question Summary</h3>
                    <div className="form-group">
                        <label>If we celebrate your progress 5 years from today, what would have happened for you to feel truly proud?</label>
                        <textarea className="summary-textarea" value={formData.summary.celebrationProgress} onChange={(e) => handleChange('summary', 'celebrationProgress', e.target.value)} placeholder="Type your vision here..." />
                    </div>
                </div>
            );
        }

        if (currentStep === 3) {
            return (
                <div className="doc-card-accordion">
                    {/* Part 1: Loans */}
                    <div className={`accordion-group ${expandedSections.includes(1) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(1)}>
                            <h3>1. Loans (Debt)</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="loan-sub-section">
                                <label className="cat-label"><strong>Home Loan:</strong></label>
                                <div className="form-grid three-col">
                                    <div className="form-group"><label>Outstanding Amount</label><input type="number" value={formData.section3.homeLoanAmount} onChange={(e) => handleChange('section3', 'homeLoanAmount', e.target.value)} /></div>
                                    <div className="form-group"><label>Interest Rate (%)</label><input type="number" step="0.1" value={formData.section3.homeLoanInterest} onChange={(e) => handleChange('section3', 'homeLoanInterest', e.target.value)} /></div>
                                    <div className="form-group"><label>Monthly EMI</label><input type="number" value={formData.section3.homeLoanEMI} onChange={(e) => handleChange('section3', 'homeLoanEMI', e.target.value)} /></div>
                                </div>
                            </div>
                            <div className="loan-sub-section" style={{marginTop: '1.5rem'}}>
                                <label className="cat-label"><strong>Personal / Car Loan:</strong></label>
                                <div className="form-grid three-col">
                                    <div className="form-group"><label>Outstanding Amount</label><input type="number" value={formData.section3.personalLoanAmount} onChange={(e) => handleChange('section3', 'personalLoanAmount', e.target.value)} /></div>
                                    <div className="form-group"><label>Interest Rate (%)</label><input type="number" step="0.1" value={formData.section3.personalLoanInterest} onChange={(e) => handleChange('section3', 'personalLoanInterest', e.target.value)} /></div>
                                    <div className="form-group"><label>Monthly EMI</label><input type="number" value={formData.section3.personalLoanEMI} onChange={(e) => handleChange('section3', 'personalLoanEMI', e.target.value)} /></div>
                                </div>
                            </div>
                            <div className="form-group" style={{marginTop: '1.5rem'}}>
                                <label><strong>Credit Cards:</strong> Outstanding dues (if any)</label>
                                <input type="number" value={formData.section3.creditCardDues} onChange={(e) => handleChange('section3', 'creditCardDues', e.target.value)} placeholder="Total outstanding balance across all cards" />
                            </div>
                        </div>
                    </div>

                    {/* Part 2: Investments */}
                    <div className={`accordion-group ${expandedSections.includes(2) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(2)}>
                            <h3>2. Investments</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label><strong>EPF:</strong> Contributing to Provident Fund?</label>
                                    <select value={formData.section3.epf} onChange={(e) => handleChange('section3', 'epf', e.target.value)}>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label><strong>PPF:</strong> Contribution Amount (Monthly/Annual)</label>
                                    <input type="number" value={formData.section3.ppfAmount} onChange={(e) => handleChange('section3', 'ppfAmount', e.target.value)} />
                                </div>
                            </div>
                            <div className="form-grid" style={{marginTop: '1rem'}}>
                                <div className="form-group">
                                    <label><strong>Mutual Funds/SIPs:</strong> Total Monthly Amount</label>
                                    <input type="number" value={formData.section3.sipAmount} onChange={(e) => handleChange('section3', 'sipAmount', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label>Type of Funds</label>
                                    <input type="text" value={formData.section3.sipFundType} onChange={(e) => handleChange('section3', 'sipFundType', e.target.value)} placeholder="e.g. Large-cap, Mid-cap, Hybrid" />
                                </div>
                            </div>
                            <div className="form-grid" style={{marginTop: '1rem'}}>
                                <div className="form-group">
                                    <label><strong>Stocks:</strong> Total Current Value</label>
                                    <input type="number" value={formData.section3.stocksAmount} onChange={(e) => handleChange('section3', 'stocksAmount', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label><strong>Fixed Deposits/Bonds:</strong> Total Value</label>
                                    <input type="number" value={formData.section3.fdBondsAmount} onChange={(e) => handleChange('section3', 'fdBondsAmount', e.target.value)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Part 3: Insurance */}
                    <div className={`accordion-group ${expandedSections.includes(3) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(3)}>
                            <h3>3. Insurance</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="ins-sub-section">
                                <label className="cat-label"><strong>Life Insurance:</strong></label>
                                <div className="form-grid three-col">
                                    <div className="form-group">
                                        <label>Type</label>
                                        <select value={formData.section3.lifeInsuranceType} onChange={(e) => handleChange('section3', 'lifeInsuranceType', e.target.value)}>
                                            <option value="Term">Term Insurance</option>
                                            <option value="ULIP">ULIP</option>
                                            <option value="Endowment">Endowment</option>
                                        </select>
                                    </div>
                                    <div className="form-group"><label>Cover Amount (Sum Assured)</label><input type="number" value={formData.section3.lifeInsuranceCover} onChange={(e) => handleChange('section3', 'lifeInsuranceCover', e.target.value)} /></div>
                                    <div className="form-group"><label>Annual Premium</label><input type="number" value={formData.section3.lifeInsurancePremium} onChange={(e) => handleChange('section3', 'lifeInsurancePremium', e.target.value)} /></div>
                                </div>
                            </div>
                            <div className="ins-sub-section" style={{marginTop: '2rem'}}>
                                <label className="cat-label"><strong>Health Insurance:</strong></label>
                                <div className="form-grid three-col">
                                    <div className="form-group">
                                        <label>Plan Type</label>
                                        <select value={formData.section3.healthInsuranceType} onChange={(e) => handleChange('section3', 'healthInsuranceType', e.target.value)}>
                                            <option value="Individual">Individual</option>
                                            <option value="Family Floater">Family Floater</option>
                                        </select>
                                    </div>
                                    <div className="form-group"><label>Cover Amount</label><input type="number" value={formData.section3.healthInsuranceCover} onChange={(e) => handleChange('section3', 'healthInsuranceCover', e.target.value)} /></div>
                                    <div className="form-group"><label>Annual Premium</label><input type="number" value={formData.section3.healthInsurancePremium} onChange={(e) => handleChange('section3', 'healthInsurancePremium', e.target.value)} /></div>
                                </div>
                                <div className="form-group" style={{marginTop: '1rem'}}>
                                    <label>Are your parents' health insured?</label>
                                    <select value={formData.section3.healthInsuranceParentsRecord} onChange={(e) => handleChange('section3', 'healthInsuranceParentsRecord', e.target.value)}>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 4) {
            return (
                <div className="doc-card-accordion">
                    {/* Part 1: Emergency Fund */}
                    <div className={`accordion-group ${expandedSections.includes(1) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(1)}>
                            <h3>1. Emergency Fund</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Do you have a separate emergency fund?</label>
                                <select value={formData.section4.emergencyFund} onChange={(e) => handleChange('section4', 'emergencyFund', e.target.value)}>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>
                            {formData.section4.emergencyFund === 'Yes' && (
                                <div className="form-grid three-col" style={{marginTop: '1rem'}}>
                                    <div className="form-group"><label>Current Amount</label><input type="number" value={formData.section4.emergencyFundAmount} onChange={(e) => handleChange('section4', 'emergencyFundAmount', e.target.value)} /></div>
                                    <div className="form-group"><label>Months Covered</label><input type="number" value={formData.section4.emergencyFundMonths} onChange={(e) => handleChange('section4', 'emergencyFundMonths', e.target.value)} /></div>
                                    <div className="form-group">
                                        <label>Storage Location</label>
                                        <select value={formData.section4.emergencyFundStorage} onChange={(e) => handleChange('section4', 'emergencyFundStorage', e.target.value)}>
                                            <option value="Savings">Savings Account</option>
                                            <option value="FD">Fixed Deposit</option>
                                            <option value="Cash">Cash</option>
                                            <option value="Liquid Fund">Liquid Fund</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Part 2: Specific Coverages */}
                    <div className={`accordion-group ${expandedSections.includes(2) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(2)}>
                            <h3>2. Critical Illness / Accident Cover</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Do you have Critical Illness insurance?</label>
                                    <select value={formData.section4.criticalIllnessCover} onChange={(e) => handleChange('section4', 'criticalIllnessCover', e.target.value)}>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Do you have Accident insurance?</label>
                                    <select value={formData.section4.accidentCover} onChange={(e) => handleChange('section4', 'accidentCover', e.target.value)}>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 5) {
            return (
                <div className="doc-card-accordion">
                    {/* Part 1: Short-Term */}
                    <div className={`accordion-group ${expandedSections.includes(1) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(1)}>
                            <h3>1. Short-Term Goals (1-3 Years)</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>What are your major financial goals in the next 1-3 years?</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.section5.shortTermGoals} 
                                    onChange={(e) => handleChange('section5', 'shortTermGoals', e.target.value)} 
                                    placeholder="e.g. Buying a car, dream vacation, wedding fund, emergency fund..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 2: Medium-Term */}
                    <div className={`accordion-group ${expandedSections.includes(2) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(2)}>
                            <h3>2. Medium-Term Goals (3-7 Years)</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>What do you want to achieve in the next 3-7 years?</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.section5.mediumTermGoals} 
                                    onChange={(e) => handleChange('section5', 'mediumTermGoals', e.target.value)} 
                                    placeholder="e.g. Down payment for a house, start a business, educational goals..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 3: Long-Term */}
                    <div className={`accordion-group ${expandedSections.includes(3) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(3)}>
                            <h3>3. Long-Term Goals (7+ Years)</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="goal-sub-section">
                                <label className="cat-label"><strong>Retirement Planning:</strong></label>
                                <div className="form-grid three-col">
                                    <div className="form-group"><label>Desired Retirement Age</label><input type="number" value={formData.section5.retirementAge} onChange={(e) => handleChange('section5', 'retirementAge', e.target.value)} /></div>
                                    <div className="form-group">
                                        <label>Lifestyle Envisioned</label>
                                        <select value={formData.section5.retirementLifestyle} onChange={(e) => handleChange('section5', 'retirementLifestyle', e.target.value)}>
                                            <option value="Simple">Simple</option>
                                            <option value="Comfortable">Comfortable</option>
                                            <option value="Luxurious">Luxurious</option>
                                        </select>
                                    </div>
                                    <div className="form-group"><label>Target Corpus (Goal Amount)</label><input type="number" value={formData.section5.retirementCorpus} onChange={(e) => handleChange('section5', 'retirementCorpus', e.target.value)} /></div>
                                </div>
                            </div>
                            <div className="goal-sub-section" style={{marginTop: '2rem'}}>
                                <label className="cat-label"><strong>Children's Future (if applicable):</strong></label>
                                <div className="form-group">
                                    <label>Saving goals for their college? (In India or Abroad?)</label>
                                    <textarea className="roadmap-textarea" value={formData.section5.childrenFuturePlans} onChange={(e) => handleChange('section5', 'childrenFuturePlans', e.target.value)} placeholder="e.g. Yes, saving for medical studies in India..." />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Part 4: Prioritization */}
                    <div className={`accordion-group ${expandedSections.includes(4) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(4)}>
                            <h3>4. Goal Prioritization</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>If you had to rank these goals, which one feels most important to you right now?</label>
                                <input type="text" value={formData.section5.topGoalsRank} onChange={(e) => handleChange('section5', 'topGoalsRank', e.target.value)} placeholder="Type your #1 priority here..." />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 6) {
            return (
                <div className="doc-card-accordion">
                    {/* Part 1: Risk & Mindset */}
                    <div className={`accordion-group ${expandedSections.includes(1) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(1)}>
                            <h3>1. Risk Tolerance (The 10% Test)</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>How would you feel if your investments dropped 10% in a year?</label>
                                <div className="multi-select-group">
                                    {['Panic & Sell', 'Hold On', 'Buying Opportunity'].map(reaction => (
                                        <button key={reaction} type="button" className={`doc-pill ${formData.section6.investmentDropReaction === reaction ? 'active' : ''}`} onClick={() => handleChange('section6', 'investmentDropReaction', reaction)}>{reaction}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Part 2: Knowledge & Experiences */}
                    <div className={`accordion-group ${expandedSections.includes(2) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(2)}>
                            <h3>2. Knowledge & Past Experiences</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Financial Knowledge Rating (1-5)</label>
                                <div className="rating-scale-wrap" style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                                    {[1,2,3,4,5].map(num => (
                                        <button key={num} type="button" className={`rating-btn ${formData.section6.financialKnowledge === num ? 'active' : ''}`} style={{ width: '45px', height: '45px', borderRadius: '50%', border: '1px solid #ccc', background: formData.section6.financialKnowledge === num ? '#003a70' : 'white', color: formData.section6.financialKnowledge === num ? 'white' : '#003a70', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleChange('section6', 'financialKnowledge', num)}>{num}</button>
                                    ))}
                                </div>
                                <p className="rating-hint" style={{marginTop: '0.8rem', fontSize: '0.85rem', color: '#64748b'}}>(Scale: 1 = Basic, 5 = Advanced understanding of Mutual Funds, PPF, Taxes etc.)</p>
                            </div>
                            <div className="form-group" style={{marginTop: '2.5rem'}}>
                                <label>Past Financial Regrets or Best Decisions?</label>
                                <textarea className="roadmap-textarea" value={formData.section6.pastMistake} onChange={(e) => handleChange('section6', 'pastMistake', e.target.value)} placeholder="What did you learn from your past financial experiences? Any regrets or major wins?" />
                            </div>
                        </div>
                    </div>

                    {/* Part 3: Tax Profile */}
                    <div className={`accordion-group ${expandedSections.includes(3) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(3)}>
                            <h3>3. Tax Regime</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Preferred Tax Regime</label>
                                    <select value={formData.section7.taxRegime} onChange={(e) => handleChange('section7', 'taxRegime', e.target.value)}>
                                        <option value="New">New Tax Regime</option>
                                        <option value="Old">Old Tax Regime</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Declared to HR already?</label>
                                    <select value={formData.section7.declaredToHR} onChange={(e) => handleChange('section7', 'declaredToHR', e.target.value)}>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 7) {
            return (
                <div className="doc-card-accordion">
                    {/* Part 1: Perfect Day */}
                    <div className={`accordion-group ${expandedSections.includes(1) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(1)}>
                            <h3>1. The Perfect Ordinary Tuesday</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Imagine you are financially free, 5-10 years in the future. Describe your perfect Tuesday: Where do you wake up? Who are you with? How do you spend your time?</label>
                                <textarea className="roadmap-textarea" value={formData.deepDiveA.perfectDay} onChange={(e) => handleChange('deepDiveA', 'perfectDay', e.target.value)} placeholder="Describe your ideal day here..." />
                            </div>
                        </div>
                    </div>

                    {/* Part 2: Financial Freedom */}
                    <div className={`accordion-group ${expandedSections.includes(2) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(2)}>
                            <h3>2. Your Meaning of "Financial Freedom"</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Retiring early? Traveling? Or never worrying about bills again? What does this phrase personally mean to you?</label>
                                <textarea className="roadmap-textarea" value={formData.deepDiveA.financialFreedomMeaning} onChange={(e) => handleChange('deepDiveA', 'financialFreedomMeaning', e.target.value)} placeholder="Define your freedom..." />
                            </div>
                        </div>
                    </div>

                    {/* Part 3: Magic Wand */}
                    <div className={`accordion-group ${expandedSections.includes(3) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(3)}>
                            <h3>3. The Magic Wand</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>If money were no object, what is the ONE thing you would do or change about your life right now?</label>
                                <textarea className="roadmap-textarea" value={formData.deepDiveA.magicWand} onChange={(e) => handleChange('deepDiveA', 'magicWand', e.target.value)} placeholder="What is your deep-seated aspiration?" />
                            </div>
                        </div>
                    </div>

                    {/* Part 4: Worry */}
                    <div className={`accordion-group ${expandedSections.includes(4) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(4)}>
                            <h3>4. What Keeps You Awake?</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>What is your biggest financial worry or source of stress? (e.g., job loss, education costs, feeling behind peers...)</label>
                                <textarea className="roadmap-textarea" value={formData.deepDiveA.biggestWorry} onChange={(e) => handleChange('deepDiveA', 'biggestWorry', e.target.value)} placeholder="Share your concerns..." />
                            </div>
                        </div>
                    </div>

                    {/* Part 5: Rich Definition */}
                    <div className={`accordion-group ${expandedSections.includes(5) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(5)}>
                            <h3>5. What Makes You Feel "Rich"?</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Is it a paid-off home? Generosity? Buying books without checking the price? What is it for you?</label>
                                <textarea className="roadmap-textarea" value={formData.deepDiveA.whatMakesYouFeelRich} onChange={(e) => handleChange('deepDiveA', 'whatMakesYouFeelRich', e.target.value)} placeholder="Describe your 'rich' feeling..." />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 8) {
            return (
                <div className="doc-card-accordion">
                    {/* Part 1: Travel */}
                    <div className={`accordion-group ${expandedSections.includes(1) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(1)}>
                            <h3>1. Travel & Experiences</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>What's a dream trip you've always wanted to take? (e.g., month in Europe, pilgrimage...). What does "experiences over things" mean to you?</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveB.dreamTrip} 
                                    onChange={(e) => handleChange('deepDiveB', 'dreamTrip', e.target.value)} 
                                    placeholder="Describe your dream travel experiences..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 2: Family */}
                    <div className={`accordion-group ${expandedSections.includes(2) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(2)}>
                            <h3>2. Family & Relationships</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label><strong>For Parents:</strong> Beyond basic support, is there a special dream you have for them? (Vacation, home renovation, medical treatment...)</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveB.parentsDream} 
                                    onChange={(e) => handleChange('deepDiveB', 'parentsDream', e.target.value)} 
                                    placeholder="Your dreams for your parents..." 
                                />
                            </div>
                            <div className="form-group" style={{marginTop: '1.5rem'}}>
                                <label><strong>For Partner/Children:</strong> What kind of special experiences do you want to create? (Education, hobbies, big gatherings...)</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveB.partnerChildrenExperiences} 
                                    onChange={(e) => handleChange('deepDiveB', 'partnerChildrenExperiences', e.target.value)} 
                                    placeholder="Memorable experiences with family..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 3: Career */}
                    <div className={`accordion-group ${expandedSections.includes(3) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(3)}>
                            <h3>3. Career & Purpose</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Do you see yourself doing the same job 10 years from now? If not, what would you rather be doing?</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveB.career10Years} 
                                    onChange={(e) => handleChange('deepDiveB', 'career10Years', e.target.value)} 
                                    placeholder="Your future career vision..." 
                                />
                            </div>
                            <div className="form-group" style={{marginTop: '1.5rem'}}>
                                <label>Is there a passion project, side hustle, or social cause you'd love to dedicate time to with financial flexibility?</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveB.passionProject} 
                                    onChange={(e) => handleChange('deepDiveB', 'passionProject', e.target.value)} 
                                    placeholder="Describe your passion or side hustle goals..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 4: Home */}
                    <div className={`accordion-group ${expandedSections.includes(4) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(4)}>
                            <h3>4. Home & Environment</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>What is your ideal living situation? (Bigger house, farmhouse, specific neighborhood...)</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveB.idealHome} 
                                    onChange={(e) => handleChange('deepDiveB', 'idealHome', e.target.value)} 
                                    placeholder="Describe your vision for your living space..." 
                                />
                            </div>
                            <div className="form-group" style={{marginTop: '1.5rem'}}>
                                <label>Is there a specific "thing" you want to own that represents a milestone for you? (Car, art, workshop...)</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveB.representationMilestone} 
                                    onChange={(e) => handleChange('deepDiveB', 'representationMilestone', e.target.value)} 
                                    placeholder="The 'milestone' object you desire..." 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 9) {
            return (
                <div className="doc-card-accordion">
                    {/* Part 1: Childhood Lessons */}
                    <div className={`accordion-group ${expandedSections.includes(1) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(1)}>
                            <h3>1. Money Roots (Growing Up)</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>What did you learn about money growing up? Was it stress or security? How does it shape your attitude today?</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveC.childhoodMoneyLearning} 
                                    onChange={(e) => handleChange('deepDiveC', 'childhoodMoneyLearning', e.target.value)} 
                                    placeholder="Describe your early money experiences..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 2: Biggest Fear */}
                    <div className={`accordion-group ${expandedSections.includes(2) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(2)}>
                            <h3>2. Your Biggest Financial Fear</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Is it running out of money in old age? Burdening family? Failing children's goals? Something else?</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveC.biggestFinancialFear} 
                                    onChange={(e) => handleChange('deepDiveC', 'biggestFinancialFear', e.target.value)} 
                                    placeholder="Describe your biggest financial worry..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 3: Generosity */}
                    <div className={`accordion-group ${expandedSections.includes(3) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(3)}>
                            <h3>3. Generosity & Legacy</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Important to support a cause? Help siblings? Leave something behind? What does it look like?</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveC.generosityLegacy} 
                                    onChange={(e) => handleChange('deepDiveC', 'generosityLegacy', e.target.value)} 
                                    placeholder="Your legacy and generosity vision..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 4: FOMO */}
                    <div className={`accordion-group ${expandedSections.includes(4) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(4)}>
                            <h3>4. FOMO (Fear Of Missing Out)</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Feel pressure to keep up with friends? Gadgets, cars, vacations? What triggers it?</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveC.fomoPressure} 
                                    onChange={(e) => handleChange('deepDiveC', 'fomoPressure', e.target.value)} 
                                    placeholder="Identify your spending pressures..." 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 10) {
            return (
                <div className="doc-card-accordion">
                    {/* Part 1: Perfect Execution */}
                    <div className={`accordion-group ${expandedSections.includes(1) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(1)}>
                            <h3>1. If Our Plan Works Perfectly...</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>What will it have enabled you to do in 5 years that you can't do today? (e.g., sabbatical, starting a business, move to new city...)</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveD.enabling5Years} 
                                    onChange={(e) => handleChange('deepDiveD', 'enabling5Years', e.target.value)} 
                                    placeholder="Describe your 5-year freedom goal..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 2: Timeline */}
                    <div className={`accordion-group ${expandedSections.includes(2) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(2)}>
                            <h3>2. Putting a Timeline on Dreams</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>Roughly when do these happen?<br/>• Travel dream (2yr or 10yr?)<br/>• Career change (3yr?)<br/>• Supporting parents (Immediate or 5yr?)</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveD.timelineDreams} 
                                    onChange={(e) => handleChange('deepDiveD', 'timelineDreams', e.target.value)} 
                                    placeholder="List your rough timeline here..." 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Part 3: Trade-offs */}
                    <div className={`accordion-group ${expandedSections.includes(3) ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleSection(3)}>
                            <h3>3. The Big Trade-offs</h3>
                            <div className="acc-icon-wrap"><span className="acc-bar-h" /><span className="acc-bar-v" /></div>
                        </div>
                        <div className="accordion-content">
                            <div className="form-group">
                                <label>If we had to prioritize your top 3 dreams for the next 5 years, which would they be? (This helps in allocating resources effectively).</label>
                                <textarea 
                                    className="roadmap-textarea"
                                    value={formData.deepDiveD.top3Tradeoffs} 
                                    onChange={(e) => handleChange('deepDiveD', 'top3Tradeoffs', e.target.value)} 
                                    placeholder="Your top 3 priority dreams..." 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Fallback for other steps
        return (
            <div className="doc-card">
                <p className="doc-hint">Complete the details for the <strong>{step.title}</strong> phase.</p>
                <textarea 
                    className="roadmap-big-textarea"
                    placeholder={`Enter details about ${step.title.toLowerCase()}...`}
                    value={formData[`deepDive${currentStep}`] || ""}
                    onChange={(e) => handleChange(`deepDive${currentStep}`, 'data', e.target.value)}
                />
            </div>
        );
    };

    return (
        <div className="roadmap-form-wrapper roadmap-theme">
            <div className="roadmap-form-container">
                <div className="roadmap-header-stats">
                    <div className="roadmap-progress-badge">
                        <div className="perc-label">{completionPercent}% COMPLETE</div>
                        <div className="mini-prog"><div className="inner-fill" style={{width: `${completionPercent}%`}} /></div>
                    </div>
                    {lastSaved && <div className="last-saved"><MdSave /> Progress Protected: {lastSaved}</div>}
                </div>

                <div className="roadmap-stepper-container">
                    <div className="roadmap-stepper-track">
                        {steps.map((s, idx) => (
                            <div key={idx} className={`roadmap-node ${currentStep === idx + 1 ? 'active' : ''} ${currentStep > idx + 1 ? 'completed' : ''}`}>
                                <div className="node-number">{currentStep > idx + 1 ? '✓' : idx + 1}</div>
                                <span className="node-label-small">{s.short}</span>
                            </div>
                        ))}
                    </div>
                    <div className="roadmap-stepper-progress"><div className="roadmap-progress-fill" style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }} /></div>
                </div>

                <div className="roadmap-content-surface">
                    <form onSubmit={handleSubmit}>
                        <div className="doc-section-header">
                            <h2 className="doc-title">{steps[currentStep - 1].title}</h2>
                            <span className="doc-subtitle">{steps[currentStep - 1].subtitle}</span>
                        </div>

                        {renderStepContent()}

                        <div className="roadmap-actions">
                            {currentStep > 1 && (<button type="button" onClick={prevStep} className="btn-roadmap-back"><MdArrowBack /> Previous</button>)}
                            {currentStep < totalSteps ? (
                                <button type="button" onClick={nextStep} className="btn-roadmap-next">Next Section <MdArrowForward /></button>
                            ) : (
                                <button type="submit" className="btn-roadmap-submit" disabled={isSubmitting}>{isSubmitting ? 'Submitting...' : 'Submit Full Roadmap'}</button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RoadmapForm;
